import { useEffect, useMemo, useState } from 'react';
import { MapPin, Plus, Trash2, Truck as TruckIcon, Warehouse as WarehouseIcon } from 'lucide-react';
import { useOrgWarehouses, type WarehouseInput } from '../../../hooks/useOrgWarehouses';
import { useOrgTrucks, type TruckInput } from '../../../hooks/useOrgTrucks';
import { CustomDropdown } from '../../../components/ui/CustomDropdown';
import { Combobox } from '../../../components/ui/Combobox';
import { AddressSearch, type SelectedPlace } from '../../../components/ui/AddressSearch';
import type { Organization, WarehouseType, Warehouse, Truck, TruckFuelType } from '../../../types/admin';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { ConfirmModal } from '../../../components/admin/ConfirmModal';
import { useFleetDefaultTruckTypes, useFleetDefaultMakesModels } from '../../../hooks/useFleetDefaults';
import { PORTAL_TO_PORTAL_NO_WAREHOUSE_WARNING, resolveOrgTravelTimeRate } from '@iomi/shared';

// ── Constants ───���────────────────────────────────────────────────────────

/** Legacy labels for backward compatibility. */
const LEGACY_TRUCK_TYPE_LABELS: Record<string, string> = {
  cargo_van: 'Cargo Van',
  sprinter_van: 'Sprinter Van',
  '12ft_box': '12ft Box Truck',
  '16ft_box': '16ft Box Truck',
  '20ft_box': '20ft Box Truck',
  '24ft_box': '24ft Box Truck',
  '26ft_box': '26ft Box Truck',
  flatbed: 'Flatbed',
  other: 'Other',
};

const WAREHOUSE_TYPE_LABELS: Record<WarehouseType, string> = {
  owned: 'Owned',
  rented: 'Rented',
  partner: 'Partner',
};

// ── Props ────────────────────────────────────────────────────────────────

interface FleetStepProps {
  org: Organization;
  orgId: string;
  /** Fires with the name of an Add form that is open (its contents are not saved until Add is pressed), or null. */
  onOpenFormChange?: (openForm: string | null) => void;
}

// ── Component ────────────────────────────────────────────────────────────

export function FleetStep({ org, orgId, onOpenFormChange }: FleetStepProps) {
  const fleetEnabled = org.featureFleetManagement === true;
  const { warehouses, loading: whLoading, createWarehouse, deleteWarehouse } = useOrgWarehouses(orgId);
  const { trucks, loading: trLoading, createTruck, deleteTruck } = useOrgTrucks(orgId);
  const [warehouseFormOpen, setWarehouseFormOpen] = useState(false);
  const [truckFormOpen, setTruckFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ kind: 'warehouse' | 'truck'; id: string; name: string } | null>(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    onOpenFormChange?.(warehouseFormOpen ? 'Add Warehouse' : truckFormOpen ? 'Add Truck' : null);
  }, [warehouseFormOpen, truckFormOpen, onOpenFormChange]);

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    setDeleteError('');
    try {
      if (target.kind === 'warehouse') await deleteWarehouse(target.id);
      else await deleteTruck(target.id);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : `Failed to delete ${target.kind}`);
    }
  }

  const canAddMoreWarehouses = fleetEnabled || warehouses.length === 0;
  const canAddMoreTrucks = fleetEnabled || trucks.length === 0;

  // Portal to Portal travel time is computed from the nearest warehouse; with
  // none, the formula bills 0 travel hours and nothing says so. Warn here,
  // where the warehouse gets added.
  const usesPortalToPortal = warehouses.length === 0 && (
    (org.featureCommercialMovingQuotes === true && resolveOrgTravelTimeRate(org, 'commercial') === 'portal_to_portal')
    || (org.featureResidentialMovingQuotes === true && resolveOrgTravelTimeRate(org, 'residential') === 'portal_to_portal')
  );

  if (whLoading || trLoading) {
    return <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Explainer */}
      <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
        <p className="text-sm text-blue-800">
          {fleetEnabled
            ? 'Adding your warehouse locations and trucks helps IOMI Pro estimate transit times to and from job sites, recommend the right trucks for each job, and track fleet availability across your operation. You can add more warehouses and trucks anytime in Settings.'
            : 'Adding your primary warehouse and truck helps IOMI Pro estimate transit times to and from job sites and recommend the right truck size for each job. Upgrade your plan to unlock full fleet management with multiple warehouses, trucks, and availability tracking.'}
        </p>
      </div>

      {usesPortalToPortal && (
        <div role="alert" className="rounded-xl bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-900">{PORTAL_TO_PORTAL_NO_WAREHOUSE_WARNING}</p>
        </div>
      )}

      {/* Warehouses */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <WarehouseIcon className="w-4 h-4 text-slate" />
            <h3 className="text-sm font-semibold text-midnight">Warehouses</h3>
            {warehouses.length > 0 && (
              <span className="text-xs text-slate/70">({warehouses.length})</span>
            )}
          </div>
        </div>

        {deleteError && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600">
            {deleteError}
          </div>
        )}

        {warehouses.length > 0 && (
          <div className="space-y-2 mb-3">
            {warehouses.map((w) => (
              <WarehouseCard key={w.id} warehouse={w} onDelete={() => setPendingDelete({ kind: 'warehouse', id: w.id, name: w.name })} />
            ))}
          </div>
        )}

        {canAddMoreWarehouses && (
          <AddWarehouseForm onAdd={createWarehouse} onOpenChange={setWarehouseFormOpen} />
        )}
        {!canAddMoreWarehouses && (
          <p className="text-xs text-slate/70 italic">
            Upgrade your plan to add more warehouses.
          </p>
        )}
        <p className="text-xs text-slate/70 mt-2">
          You can add more warehouses and manage details for each in the Admin Panel later.
        </p>
      </section>

      {/* Trucks */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TruckIcon className="w-4 h-4 text-slate" />
            <h3 className="text-sm font-semibold text-midnight">Trucks</h3>
            {trucks.length > 0 && (
              <span className="text-xs text-slate/70">({trucks.length})</span>
            )}
          </div>
        </div>

        {trucks.length > 0 && (
          <div className="space-y-2 mb-3">
            {trucks.map((t) => (
              <TruckCard key={t.id} truck={t} onDelete={() => setPendingDelete({ kind: 'truck', id: t.id, name: t.name })} />
            ))}
          </div>
        )}

        {canAddMoreTrucks && (
          <AddTruckForm onAdd={createTruck} onOpenChange={setTruckFormOpen} />
        )}
        {!canAddMoreTrucks && (
          <p className="text-xs text-slate/70 italic">
            Upgrade your plan to add more trucks.
          </p>
        )}
        <p className="text-xs text-slate/70 mt-2">
          You can add more trucks and manage details for each in the Admin Panel later.
        </p>
      </section>

      <ConfirmModal
        isOpen={pendingDelete !== null}
        title={pendingDelete?.kind === 'truck' ? 'Delete this truck?' : 'Delete this warehouse?'}
        message={`${pendingDelete?.name ?? ''} will be removed from your ${pendingDelete?.kind === 'truck' ? 'fleet' : 'warehouses'}. This can't be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ── Warehouse card ─���─────────────────────────────────────────────────────

function WarehouseCard({ warehouse, onDelete }: { warehouse: Warehouse; onDelete: () => void }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-stone bg-mist">
      <MapPin className="w-4 h-4 text-slate/70 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-midnight truncate">{warehouse.name}</p>
        <p className="text-xs text-slate/70 truncate">{warehouse.address}</p>
      </div>
      {warehouse.type && (
        <span className="text-xs text-slate/70 bg-stone/30 px-2 py-0.5 rounded-full">
          {WAREHOUSE_TYPE_LABELS[warehouse.type]}
        </span>
      )}
      <button
        onClick={onDelete}
        className="p-1.5 rounded-lg hover:bg-red-50 text-slate/70 hover:text-red-600 transition-colors"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

// ── Truck card ───────────────────────────────────────────────────────────

function TruckCard({ truck, onDelete }: { truck: Truck; onDelete: () => void }) {
  const descParts: string[] = [];
  if (truck.year) descParts.push(String(truck.year));
  if (truck.make) descParts.push(truck.make);
  if (truck.model) descParts.push(truck.model);

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-stone bg-mist">
      <TruckIcon className="w-4 h-4 text-slate/70 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-midnight truncate">{truck.name}</p>
        {truck.type && (
          <p className="text-xs text-slate/70">{LEGACY_TRUCK_TYPE_LABELS[truck.type] ?? truck.type}</p>
        )}
        {descParts.length > 0 && (
          <p className="text-xs text-slate/70">{descParts.join(' ')}</p>
        )}
      </div>
      <button
        onClick={onDelete}
        className="p-1.5 rounded-lg hover:bg-red-50 text-slate/70 hover:text-red-600 transition-colors"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

// ── Add warehouse form ───────────────────────────────────────────────────

function AddWarehouseForm({ onAdd, onOpenChange }: {
  onAdd: (input: WarehouseInput) => Promise<Warehouse>;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => { setOpenState(next); onOpenChange?.(next); };
  const [name, setName] = useState('');
  const [address, setAddress] = useState<SelectedPlace | null>(null);
  const [type, setType] = useState<WarehouseType>('owned');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleAdd() {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!address) { setError('Address is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onAdd({
        name: name.trim(),
        address: address.address,
        placeId: address.placeId,
        note: '',
        type,
        status: 'active',
      });
      setName('');
      setAddress(null);
      setType('owned');
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add warehouse');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-plum border border-dashed border-plum/40 rounded-xl hover:bg-plum/5 transition-colors w-full justify-center"
      >
        <Plus size={16} />
        Add Warehouse
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-stone bg-mist p-4 space-y-3">
      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600">
          {error}
        </div>
      )}
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Warehouse Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Main Warehouse"
          className="w-full px-3 py-2 text-sm text-midnight bg-white border border-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-plum/20"
        />
      </div>
      <div>
        <AddressSearch value={address} onChange={setAddress} label="Address" required />
      </div>
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Type</label>
        <CustomDropdown
          value={type}
          onChange={(v) => setType(v as WarehouseType)}
          options={Object.entries(WAREHOUSE_TYPE_LABELS).map(([val, label]) => ({ value: val, label }))}
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleAdd}
          disabled={saving}
          className="flex-1 px-3 py-2 text-xs font-medium text-white bg-plum hover:bg-rose rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
        <button
          onClick={() => { setOpen(false); setError(''); }}
          disabled={saving}
          className="px-3 py-2 text-xs font-medium text-slate bg-stone/30 hover:bg-mist rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Add truck form ───────────────────────────────────────────────────────

const FUEL_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '—' },
  { value: 'gas', label: 'Gas' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'electric', label: 'Electric' },
  { value: 'hybrid', label: 'Hybrid' },
];

const inputCls = 'w-full h-10 px-3 text-sm text-midnight bg-white border border-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-plum/20 placeholder:text-slate';
const labelCls = 'block text-[11px] font-bold uppercase tracking-widest text-slate mb-1';

function AddTruckForm({ onAdd, onOpenChange }: {
  onAdd: (input: TruckInput) => Promise<Truck>;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => { setOpenState(next); onOpenChange?.(next); };
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [capacity, setCapacity] = useState('');
  const [weightLimit, setWeightLimit] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { items: fleetTruckTypes } = useFleetDefaultTruckTypes();
  const { items: allMakesModels } = useFleetDefaultMakesModels();

  // Filter makes/models by selected truck type
  const filteredMakesModels = useMemo(() => {
    if (!type) return allMakesModels;
    const tt = fleetTruckTypes.find(t => t.name === type || t.id === type);
    const typeId = tt?.id ?? type;
    return allMakesModels.filter(mm => mm.compatibleTruckTypes.includes(typeId));
  }, [allMakesModels, type, fleetTruckTypes]);

  const makeOptions = useMemo(() => {
    const seen = new Set<string>();
    return filteredMakesModels
      .filter(mm => { if (seen.has(mm.make)) return false; seen.add(mm.make); return true; })
      .map((mm, idx) => ({ id: idx, name: mm.make }));
  }, [filteredMakesModels]);

  const modelOptions = useMemo(() => {
    if (!make) return filteredMakesModels.map((mm, idx) => ({ id: idx, name: mm.model }));
    return filteredMakesModels
      .filter(mm => mm.make.toLowerCase() === make.toLowerCase())
      .map((mm, idx) => ({ id: idx, name: mm.model }));
  }, [filteredMakesModels, make]);

  function handleTypeChange(newType: string) {
    setType(newType);
    if (newType) {
      const tt = fleetTruckTypes.find(t => t.name === newType || t.id === newType);
      if (tt) {
        if (!capacity) setCapacity(String(tt.defaultCapacity));
        if (!weightLimit) setWeightLimit(String(tt.defaultWeightLimit));
      }
    }
    // Clear make/model if no longer compatible
    if (make) {
      const tt = fleetTruckTypes.find(t => t.name === newType || t.id === newType);
      const typeId = tt?.id ?? newType;
      const stillValid = allMakesModels.some(
        mm => mm.compatibleTruckTypes.includes(typeId) && mm.make.toLowerCase() === make.toLowerCase(),
      );
      if (!stillValid) { setMake(''); setModel(''); }
    }
  }

  function handleMakeChange(newMake: string) {
    setMake(newMake);
    if (model) {
      const stillValid = filteredMakesModels.some(
        mm => mm.make.toLowerCase() === newMake.toLowerCase() && mm.model.toLowerCase() === model.toLowerCase(),
      );
      if (!stillValid) setModel('');
    }
  }

  function handleModelChange(newModel: string) {
    setModel(newModel);
    const mm = filteredMakesModels.find(
      m => m.make.toLowerCase() === make.toLowerCase() && m.model.toLowerCase() === newModel.toLowerCase(),
    );
    if (mm && mm.fuelTypeDefaults.length === 1 && !fuelType) {
      setFuelType(mm.fuelTypeDefaults[0]);
    }
  }

  function resetForm() {
    setName(''); setType(''); setMake(''); setModel('');
    setYear(''); setLicensePlate(''); setCapacity('');
    setWeightLimit(''); setFuelType(''); setError('');
  }

  async function handleAdd() {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const input: TruckInput = { name: name.trim(), status: 'active' };
      if (type) input.type = type;
      if (make.trim()) input.make = make.trim();
      if (model.trim()) input.model = model.trim();
      if (year) input.year = parseInt(year, 10);
      if (licensePlate.trim()) input.licensePlate = licensePlate.trim();
      if (capacity) input.capacity = parseFloat(capacity);
      if (weightLimit) input.weightLimit = parseFloat(weightLimit);
      if (fuelType) input.fuelType = fuelType as TruckFuelType;
      await onAdd(input);
      resetForm();
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add truck');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-plum border border-dashed border-plum/40 rounded-xl hover:bg-plum/5 transition-colors w-full justify-center"
      >
        <Plus size={16} />
        Add Truck
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-stone bg-mist p-4 space-y-3">
      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600">
          {error}
        </div>
      )}
      {/* Name */}
      <div>
        <label className={labelCls}>Truck Name / Identifier</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Truck 1, Big Blue"
          className={inputCls}
        />
      </div>
      {/* Type */}
      <div>
        <label className={labelCls}>Truck Type</label>
        <CustomDropdown
          value={type}
          onChange={handleTypeChange}
          options={[
            { value: '', label: '—' },
            ...fleetTruckTypes.filter(t => t.active).map(t => ({ value: t.name, label: t.name })),
          ]}
          placeholder="—"
        />
      </div>
      {/* Make + Model */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Make</label>
          <Combobox
            value={make}
            onChange={handleMakeChange}
            options={makeOptions}
            placeholder="e.g. Ford"
          />
        </div>
        <div>
          <label className={labelCls}>Model</label>
          <Combobox
            value={model}
            onChange={handleModelChange}
            options={modelOptions}
            placeholder="e.g. E-450"
          />
        </div>
      </div>
      {/* Year + License Plate */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Year</label>
          <input
            type="number"
            min={1990}
            max={new Date().getFullYear() + 2}
            value={year}
            onChange={e => setYear(e.target.value)}
            placeholder="e.g. 2022"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>License Plate</label>
          <input
            type="text"
            value={licensePlate}
            onChange={e => setLicensePlate(e.target.value)}
            placeholder="ABC-1234"
            className={inputCls}
          />
        </div>
      </div>
      {/* Capacity + Weight */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Capacity (cu ft)</label>
          <input
            type="number"
            min={0}
            value={capacity}
            onChange={e => setCapacity(e.target.value)}
            placeholder="1200"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Weight Limit (lbs)</label>
          <input
            type="number"
            min={0}
            value={weightLimit}
            onChange={e => setWeightLimit(e.target.value)}
            placeholder="10000"
            className={inputCls}
          />
        </div>
      </div>
      {/* Fuel Type */}
      <div>
        <label className={labelCls}>Fuel Type</label>
        <CustomDropdown
          value={fuelType}
          onChange={v => setFuelType(v)}
          options={FUEL_TYPE_OPTIONS}
          placeholder="—"
        />
      </div>
      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleAdd}
          disabled={saving}
          className="flex-1 px-3 py-2 text-xs font-medium text-white bg-plum hover:bg-rose rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
        <button
          onClick={() => { setOpen(false); resetForm(); }}
          disabled={saving}
          className="px-3 py-2 text-xs font-medium text-slate bg-stone/30 hover:bg-mist rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
